import React, { useState, useEffect } from "react";
import OuterBlueC from "@/assets/CarlineConsensor-Primary-Square-OuterBlueCNew.svg";
import RedSteer from "@/assets/CarlineConsensor-Primary-Square-RedSteerNew.svg";
import InnerYellowC from "@/assets/CarlineConsensor-PrimarySquare-InnerYellowCNew.svg";
import { useLogoPreload } from "@/hooks/useLogoPreload";

type AnimatedLogoProps = {
  onComplete?: () => void;
};

const AnimatedLogo: React.FC<AnimatedLogoProps> = ({ onComplete }) => {
  const assetsReady = useLogoPreload();
  const [animate, setAnimate] = useState(false);
  
  const [imagesLoaded, setImagesLoaded] = useState({
    blue: false,
    red: false,
    yellow: false,
  });
  
  const allLoaded = assetsReady && imagesLoaded.blue && imagesLoaded.red && imagesLoaded.yellow;

  useEffect(() => {
    if (!allLoaded) return;

    const t = setTimeout(() => setAnimate(true), 100);
    return () => clearTimeout(t);
  }, [allLoaded]);

  const baseImgStyle: React.CSSProperties = {
    position: "absolute",
    width: "100%",
    height: "100%",
  };

  const wheelEnterDuration = 0.8;
  const wheelTurnDuration = 3;
  const rightTurnStart = wheelEnterDuration + wheelTurnDuration * 0.45;
  const leftTurnStart = wheelEnterDuration + wheelTurnDuration * 0.8;
  const spinEnd = leftTurnStart + 3;
  const outerSpinDuration = spinEnd - rightTurnStart;
  const innerSpinDuration = spinEnd - leftTurnStart;
  const cFadeDuration = 0.6;

  useEffect(() => {
    if (!animate) return;

    const timeout = window.setTimeout(() => {
      onComplete?.();
    }, (spinEnd + 0.15) * 1000);

    return () => window.clearTimeout(timeout);
  }, [animate, onComplete, spinEnd]);

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <style>
        {`
        .center-origin {
          transform-origin: 50% 50%;
        }

        /* ================== WHEEL ANIMATIONS ================== */

        /* Steering wheel: center → RIGHT → center → LEFT → center */
        @keyframes steerFull {
          0%   { transform: rotate(0deg); }
          25%  { transform: rotate(0deg); }
          45%  { transform: rotate(60deg); }   /* RIGHT first */
          60%  { transform: rotate(0deg); }
          80%  { transform: rotate(-60deg); }  /* then LEFT */
          100% { transform: rotate(0deg); }
        }

        /* Wheel entrance: scale in with a little pulse */
        @keyframes wheelEnter {
          0%   { transform: scale(0);   opacity: 0; }
          60%  { transform: scale(1.08); opacity: 1; }
          100% { transform: scale(1);   opacity: 1; }
        }

        /* ================== C ANIMATIONS ================== */

        /* Clean 10-rotation spin — no scale pulse */
        @keyframes spinCWOnly {
          0%   { transform: rotate(0deg); }
          100% { transform: rotate(3600deg); }
        }

        @keyframes spinCCWOnly {
          0%   { transform: rotate(0deg); }
          100% { transform: rotate(-3600deg); }
        }

        /* Fade-in for Cs */
        @keyframes fadeInCs {
          0%   { opacity: 0; }
          100% { opacity: 1; }
        }
        `}
      </style>

      {/* Blue C */}
      <img
        src={OuterBlueC}
        alt=""
        className="center-origin"
        onLoad={() => setImagesLoaded(prev => ({ ...prev, blue: true }))}
        style={{
          ...baseImgStyle,
          zIndex: 1,
          opacity: 0,
          animation: animate
            ? `
              spinCWOnly ${outerSpinDuration}s cubic-bezier(0.3, 0.0, 0.1, 1.0) 1 ${rightTurnStart}s,
              fadeInCs ${cFadeDuration}s ease-out 1 ${rightTurnStart}s forwards
            `
            : "none",
        }}
      />

      {/* Red Steering Wheel */}
      <img
        src={RedSteer}
        alt=""
        className="center-origin"
        onLoad={() => setImagesLoaded(prev => ({ ...prev, red: true }))}
        style={{
          ...baseImgStyle,
          zIndex: 2,
          opacity: 0,
          animation: animate
            ? `
              wheelEnter 0.8s ease-out 1 forwards,
              steerFull 3s ease-in-out 1 0.8s
            `
            : "none",
        }}
      />

      {/* Yellow C */}
      <img
        src={InnerYellowC}
        alt=""
        className="center-origin"
        onLoad={() => setImagesLoaded(prev => ({ ...prev, yellow: true }))}
        style={{
          ...baseImgStyle,
          zIndex: 3,
          opacity: 0,
          animation: animate
            ? `
              spinCCWOnly ${innerSpinDuration}s cubic-bezier(0.3, 0.0, 0.1, 1.0) 1 ${leftTurnStart}s,
              fadeInCs ${cFadeDuration}s ease-out 1 ${leftTurnStart}s forwards
            `
            : "none",
        }}
      />
    </div>
  );
};

export default AnimatedLogo;
